<script>
    import { onDestroy, onMount } from 'svelte';
    import { push } from 'svelte-spa-router';
    import FilterButtonsForSettingsPage from "./FilterButtonsForSettingsPage.svelte";
    import NavbarReusable from "./NavbarReusable.svelte";
    import AgreementSection from "./AgreementSection.svelte";
    import { getApiBase } from './apiBase.js';
    import {
        loadTeacherWorkQrPreview,
        TEACHER_WORK_QR_ACCEPT,
        uploadTeacherWorkQrCode
    } from './teacherWorkQrApi.js';

    import { createAccountAutosave } from './accountAutosave.js';
    import { registerPersonalNotesNavigationGuard } from './personalNotesNavigation.js';

    const apiBase = getApiBase();
    let email = 'john@email.com';  // Initial email value
    let phoneNumber = '';          // Phone number value
    let receiveMessages = true;    // Checkbox for SMS notifications
    let receiveDailyReminders = true;
    
    // Teacher-specific variables
    let isTeacher = false;
    let accountLoaded = false;
    let accountLoadError = '';
    let accountNeedsLogin = false;
    let bankNr = '';
    let indivLic = '';
    let meetingLink = '';
    let desiredStudentCount = '';
    let workWithChildrenQrCode = { exists: false, uploadedAt: null, contentType: null };
    let qrCodeFile = null;
    let qrCodeInput;
    let qrCodeUploading = false;
    let qrCodeViewing = false;
    let qrCodeMessage = '';
    let qrCodeError = '';
    let qrCodePreviewUrl = '';
    let qrCodePdfUrl = '';
    let selectedSubjects = [];
    let availableSubjects = [];
    let subjectSearch = '';
    $: normalizedSubjectSearch = normalizeSearchText(subjectSearch);
    $: filteredSubjects = availableSubjects.filter(subject =>
        normalizeSearchText(subject).includes(normalizedSubjectSearch)
    );
    // Map of subject name -> grade range string (each subject has its own range)
    let subjectGrades = {};
    // Map of subject name -> validation error message
    let gradeErrors = {};
    
    // Student-specific variables
    let studentGrade = '';         // Grade for non-teacher users
    
    let autosave;
    let unregisterNavigation;
    let saveStatus = { saving: false, dirty: false, edited: false, error: '' };
    function accountValues() {
        return { email, phoneNumber, receiveMessages, receiveDailyReminders,
            ...(isTeacher ? { bankNr, indivLic, meetingLink, desiredStudentCount: desiredStudentCount == null || Number.isNaN(desiredStudentCount) ? '' : desiredStudentCount,
                teacherInfo: selectedSubjects.map(subject => ({ subject, grade: (subjectGrades[subject] || '').trim() }))
            } : { grade: studentGrade }) };
    }
    $: currentValues = { email, phoneNumber, receiveMessages, receiveDailyReminders,
        ...(isTeacher ? { bankNr, indivLic, meetingLink, desiredStudentCount: desiredStudentCount == null || Number.isNaN(desiredStudentCount) ? '' : desiredStudentCount,
            teacherInfo: selectedSubjects.map(subject => ({ subject, grade: (subjectGrades[subject] || '').trim() }))
        } : { grade: studentGrade }) };
    $: if (accountLoaded && autosave) autosave.update(currentValues);

    function validateField(key, value) {
        if (key === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Įveskite galiojantį el. pašto adresą.';
        if (key === 'meetingLink' && value) {
            try { if (!['https:', 'http:'].includes(new URL(value).protocol)) throw new Error(); }
            catch { return 'Įveskite galiojančią pamokos nuorodą.'; }
        }
        if (key === 'desiredStudentCount' && value !== '' && (!Number.isInteger(Number(value)) || Number(value) < 0)) return 'Mokinių skaičius turi būti sveikasis neneigiamas skaičius.';
        if (key === 'grade' && !/^(?:[1-9]|1[0-3]|university|universitetas)$/i.test(String(value).trim())) return 'Įveskite klasę nuo 1 iki 13 arba university.';
        if (key === 'teacherInfo') {
            gradeErrors = Object.fromEntries(value.filter(entry => !entry.grade).map(entry => [entry.subject, 'Prašome įvesti klasių intervalą']));
            if (Object.keys(gradeErrors).length) return 'Įveskite visų pasirinktų dalykų klasių intervalus.';
        }
        return '';
    }
    async function saveField(body) {
        const token = localStorage.getItem('jwtToken');
        if (!token) { requireAccountLogin(); throw new Error('Prisijunkite iš naujo.'); }
        let response;
        try {
            response = await fetch(`${apiBase}/studentplayground/settings/account/`, {
                method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });
        } catch { throw new Error('Nepavyko išsaugoti pakeitimų. Bandykite dar kartą.'); }
        if (response.status === 401) requireAccountLogin();
        if (!response.ok) {
            const error = await response.json().catch(() => ({}));
            throw new Error(error.indivLic || error.error || 'Nepavyko išsaugoti pakeitimų. Bandykite dar kartą.');
        }
    }
    function beforeLeaving(event) {
        if (autosave?.isDirty()) { void autosave.flush(); event.preventDefault(); event.returnValue = ''; }
    }
    function requireAccountLogin() {
        unregisterNavigation?.();
        localStorage.removeItem('jwtToken');
        localStorage.removeItem('jwtExpiry');
        accountNeedsLogin = true;
        accountLoadError = 'Prisijunkite iš naujo, kad galėtumėte keisti paskyros nustatymus.';
        window.dispatchEvent(new Event('tutory:session-changed'));
    }

    async function fetchInitialData() {
        accountLoaded = false;
        accountLoadError = '';
        accountNeedsLogin = false;
        console.log('🚀 fetchInitialData() called!'); // Debug: verify function runs
        const token = localStorage.getItem('jwtToken');
        if (!token) {
            requireAccountLogin();
            return;
        }
        console.log('✅ JWT token found, making API call...'); // Debug: verify token exists
        try {
            const response = await fetch(`${apiBase}/studentplayground/settings/account/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
    
            });

            if (response.status === 401) {
                requireAccountLogin();
                return;
            }
            if (response.ok) {
                const result = await response.json();
                console.log("🔍 Full API response:", result);  // Debug: see full response
                
                email = result.email;
                phoneNumber = result.phoneNumber || '';
                receiveMessages = result.receiveMessages;
                receiveDailyReminders = result.receiveDailyReminders !== false;
                isTeacher = result.isTeacher || false;

                
                // Set teacher-specific data if user is a teacher
                if (isTeacher) {
                    bankNr = result.bankNr || '';
                    indivLic = result.indivLic || '';
                    meetingLink = result.meetingLink || '';
                    desiredStudentCount = (result.desiredStudentCount ?? '') === null ? '' : (result.desiredStudentCount ?? '');
                    workWithChildrenQrCode = result.workWithChildrenQrCode || {
                        exists: false,
                        uploadedAt: null,
                        contentType: null
                    };
                    availableSubjects = result.availableSubjects || [];

                    // Build per-subject grade ranges from the teacherInfo list
                    const teacherInfo = result.teacherInfo || [];
                    const newSelected = [];
                    const newGrades = {};

                    if (teacherInfo.length > 0) {
                        for (const entry of teacherInfo) {
                            // Support both the new single `subject` and legacy `subjects` list
                            const subjectName = entry.subject ||
                                (Array.isArray(entry.subjects) ? entry.subjects[0] : null);
                            if (!subjectName) continue;
                            if (!newSelected.includes(subjectName)) {
                                newSelected.push(subjectName);
                            }
                            newGrades[subjectName] = entry.grade || '';
                        }
                    } else if (result.subjects && result.subjects.length > 0) {
                        // Legacy fallback: single grade shared across all subjects
                        for (const subjectName of result.subjects) {
                            newSelected.push(subjectName);
                            newGrades[subjectName] = result.grade || '';
                        }
                    }

                    selectedSubjects = newSelected;
                    subjectGrades = newGrades;

                    console.log("🔍 Teacher data loaded:");
                    console.log("  - selectedSubjects:", selectedSubjects);
                    console.log("  - availableSubjects:", availableSubjects);
                    console.log("  - subjectGrades:", subjectGrades);
                } else {
                    // Set student-specific data if user is not a teacher
                    studentGrade = result.grade || '';
                    
                    console.log("🔍 Student data loaded:");
                    console.log("  - studentGrade:", studentGrade);
                }
                
                autosave?.dispose();
                autosave = createAccountAutosave(accountValues(), { save: saveField, validate: validateField,
                    changed: status => { saveStatus = status; } });
                unregisterNavigation?.();
                unregisterNavigation = registerPersonalNotesNavigationGuard(autosave);
                accountLoaded = true;
                console.log("these are the data");
                console.log(result.email, result.receiveMessages, result.isTeacher);  // Success message
            } else {
                const error = await response.json();
                accountLoadError = 'Nepavyko įkelti paskyros nustatymų. Bandykite dar kartą.';
                console.log('Error:', error);
            }
        } catch (error) {
            accountLoadError = 'Nepavyko įkelti paskyros nustatymų. Bandykite dar kartą.';
            console.log('Fetch error:', error);
        }
    }
    




    // Teacher-specific functions
    function normalizeSearchText(value) {
        return value
            .toLocaleLowerCase('lt-LT')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim();
    }

    function toggleSubject(subject) {
        if (selectedSubjects.includes(subject)) {
            removeSubject(subject);
        } else {
            selectedSubjects = [...selectedSubjects, subject];
            if (!(subject in subjectGrades)) {
                subjectGrades[subject] = '';
                subjectGrades = subjectGrades;
            }
        }
    }

    function removeSubject(subject) {
        selectedSubjects = selectedSubjects.filter(s => s !== subject);
        delete subjectGrades[subject];
        subjectGrades = subjectGrades;
        if (gradeErrors[subject]) {
            delete gradeErrors[subject];
            gradeErrors = gradeErrors;
        }
    }

    function clearGradeError(subject) {
        if (gradeErrors[subject]) {
            delete gradeErrors[subject];
            gradeErrors = gradeErrors;
        }
    }

    function selectQrCode(event) {
        qrCodeFile = event.currentTarget.files?.[0] || null;
        qrCodeMessage = '';
        qrCodeError = '';
    }

    async function uploadQrCode() {
        if (!qrCodeFile) {
            qrCodeError = 'Pasirinkite QR kodo nuotrauką.';
            return;
        }
        const token = localStorage.getItem('jwtToken');
        if (!token) {
            qrCodeError = 'Norėdami įkelti QR kodą, prisijunkite iš naujo.';
            return;
        }

        qrCodeUploading = true;
        qrCodeMessage = '';
        qrCodeError = '';
        try {
            const result = await uploadTeacherWorkQrCode({
                apiBase,
                token,
                file: qrCodeFile
            });

            workWithChildrenQrCode = result.workWithChildrenQrCode;
            qrCodeMessage = result.replaced
                ? 'QR kodas sėkmingai pakeistas.'
                : 'QR kodas sėkmingai įkeltas.';
            qrCodeFile = null;
            if (qrCodeInput) qrCodeInput.value = '';
            closeQrCodePreview();
        } catch (error) {
            qrCodeError = error.message || 'Nepavyko įkelti QR kodo.';
        } finally {
            qrCodeUploading = false;
        }
    }

    async function viewQrCode() {
        const token = localStorage.getItem('jwtToken');
        if (!token) {
            qrCodeError = 'Norėdami peržiūrėti QR kodą, prisijunkite iš naujo.';
            return;
        }

        qrCodeViewing = true;
        qrCodeError = '';
        closeQrCodePreview();
        try {
            const preview = await loadTeacherWorkQrPreview({
                apiBase,
                token,
                contentType: workWithChildrenQrCode.contentType
            });
            if (preview.kind === 'pdf') {
                qrCodePdfUrl = preview.objectUrl;
            } else {
                qrCodePreviewUrl = preview.objectUrl;
            }
        } catch (error) {
            qrCodeError = error.message || 'Nepavyko parodyti QR kodo.';
        } finally {
            qrCodeViewing = false;
        }
    }

    function closeQrCodePreview() {
        if (qrCodePreviewUrl) URL.revokeObjectURL(qrCodePreviewUrl);
        if (qrCodePdfUrl) URL.revokeObjectURL(qrCodePdfUrl);
        qrCodePreviewUrl = '';
        qrCodePdfUrl = '';
    }

    function formatQrUploadDate(value) {
        return value ? new Date(value).toLocaleString('lt-LT') : '';
    }




    onMount(async () => {
        await fetchInitialData();
       
        })

    onDestroy(() => { closeQrCodePreview(); unregisterNavigation?.(); autosave?.dispose(); });

</script>

<style>
    .container {
        display: flex;
        flex-direction: column;
        margin-bottom: 20px;
        padding: 0 16px;
        gap: 20px;
        flex: none;
        color: #333;
        align-items: flex-start;
        max-width: 600px;
        margin: 0 auto;
    }
    .input-group {
        margin-top: 0;
        width: 100%;
    }
    label {
        font-size: 16px;
        font-weight: 500;
        margin-bottom: 8px;
        display: block;
        color: #333;
    }
    input[type="email"], input[type="text"], input[type="url"], input[type="number"] {
        width: 100%;
        padding: 12px;
        border: 1px solid #ccc;
        border-radius: 8px;
        background-color: #eeebdb;
        font-size: 16px;
        box-sizing: border-box;
    }
    
    input[type="email"]:focus, input[type="text"]:focus, input[type="url"]:focus, input[type="number"]:focus {
        outline: none;
        border-color: #14695b;
        box-shadow: 0 0 0 2px rgba(20, 105, 91, 0.1);
    }
    button {
        margin-top: 20px;
        height: 50px;
        width: 100%;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 12px 20px;
        border-radius: 8px;
        border: 1px solid #000;
        font-weight: 600;
        font-size: 16px;
        background-color: #fab45a;
        color: black;
        box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
        transition: background-color 0.3s ease, color 0.3s ease;
        cursor: pointer;
    }

    button:hover {
        background-color: #c98f43;
        color: black;
    }

    button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        background-color: #d4d4d4;
    }

    button:disabled:hover {
        background-color: #d4d4d4;
    }

    .autosave-status {
        margin-top: 24px;
        color: #52645a;
        font-size: 14px;
        min-height: 22px;
    }
    .checkbox-group {
        display: flex;
        flex-direction: column;
        gap: 8px;
    }
    .daily-reminders {
        border: 1px solid #c4d7ce;
        border-radius: 8px;
        background: #f4faf6;
        padding: 18px;
        margin: 18px 0;
    }
    .daily-reminders h2 { font-size: 18px; margin: 0 0 14px; color: #14695b; }
    .daily-reminders label { display: flex; align-items: flex-start; gap: 12px; font-weight: 600; cursor: pointer; }
    .daily-reminders input { flex-shrink: 0; margin-top: 2px; }
    .daily-reminders input:focus-visible { outline: 3px solid #14695b; outline-offset: 3px; }
    .daily-reminders p { margin: 12px 0 0; font-size: 14px; line-height: 1.5; color: #44534b; }
    .smaller-part {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 12px;
    }
    
    .smaller-part span {
        font-size: 15px;
        color: #555;
    }
    
    
    input[type="checkbox"] {
        width: 24px;
        height: 24px;
        border-radius: 6px;
        border: 2px solid #333;
        background-color: white;
        appearance: none;
        outline: none;
        cursor: pointer;
        box-shadow: 0px 0px 1px rgba(40, 41, 61, 0.08), 0px 0.5px 2px rgba(96, 97, 112, 0.16);
        transition: background-color 0.3s ease;
    }

    input[type="checkbox"]:checked {
        background-color: #14695B;
        border: none;
        background-image: url('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"%3E%3Cpolyline points="20 6 9 17 4 12"/%3E%3C/svg%3E');
        background-repeat: no-repeat;
        background-position: center;
    }

    /* Teacher-specific styles */
    .teacher-section {
        border-top: 1px solid #e0e0e0;
        padding-top: 24px;
        margin-top: 24px;
        width: 100%;
    }

    .section-title {
        font-size: 18px;
        font-weight: 600;
        margin-bottom: 20px;
        color: #14695B;
    }

    .subjects-container {
        max-height: 180px;
        overflow-y: auto;
        overflow-x: hidden;
        border: 1px solid #ddd;
        border-radius: 8px;
        padding: 12px;
        background-color: #f8f9fa;
        margin-top: 8px;
        margin-bottom: 15px;
        overscroll-behavior: contain;
        touch-action: pan-y;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: thin;
        user-select: none;
    }

    .subject-search {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #ccc;
        border-radius: 8px;
        background-color: white;
        font-size: 15px;
        box-sizing: border-box;
        margin: 4px 0 10px;
    }

    .subject-search:focus {
        outline: none;
        border-color: #14695b;
        box-shadow: 0 0 0 2px rgba(20, 105, 91, 0.1);
    }

    .no-subject-results {
        padding: 12px 4px;
        color: #666;
        font-size: 14px;
        text-align: center;
    }

    .subject-item {
        margin-bottom: 6px;
        background-color: white;
        border-radius: 6px;
        border: 1px solid #f0f0f0;
        transition: background-color 0.2s;
        user-select: none;
        -webkit-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
    }

    .subject-item:hover {
        background-color: #f5f5f5;
        border-color: #e0e0e0;
    }

    .subject-label {
        display: flex;
        align-items: center;
        padding: 8px;
        cursor: pointer;
        width: 100%;
        position: relative;
    }

    .subject-label input[type="checkbox"] {
        position: absolute;
        opacity: 0;
        cursor: pointer;
        height: 0;
        width: 0;
    }

    .checkmark {
        height: 24px;
        width: 24px;
        background-color: white;
        border: 2px solid #333;
        border-radius: 6px;
        margin-right: 12px;
        position: relative;
        transition: all 0.3s ease;
        flex-shrink: 0;
    }

    .subject-label input:checked ~ .checkmark {
        background-color: #14695B;
        border-color: #14695B;
    }

    .subject-label input:checked ~ .checkmark:after {
        content: "";
        position: absolute;
        display: block;
        left: 6px;
        top: 2px;
        width: 6px;
        height: 12px;
        border: solid white;
        border-width: 0 3px 3px 0;
        transform: rotate(45deg);
    }

    .subject-text {
        font-size: 15px;
        color: #444;
        user-select: none;
    }

    .subject-grades {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-top: 8px;
    }

    .subject-grade-row {
        background-color: #f8f9fa;
        border: 1px solid #ddd;
        border-radius: 8px;
        padding: 12px;
    }

    .subject-grade-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 8px;
    }

    .subject-grade-name {
        display: inline-block;
        background-color: #14695B;
        color: white;
        padding: 6px 12px;
        border-radius: 20px;
        font-size: 14px;
        font-weight: 500;
    }

    .remove-subject-grade {
        cursor: pointer;
        font-weight: bold;
        font-size: 18px;
        line-height: 1;
        color: #999;
        padding: 2px 8px;
        border-radius: 6px;
        transition: background-color 0.2s, color 0.2s;
    }

    .remove-subject-grade:hover {
        background-color: #f0f0f0;
        color: #e74c3c;
    }

    .no-subjects-hint {
        font-size: 14px;
        color: #888;
        font-style: italic;
        margin-top: 8px;
    }

    .error-message {
        color: #e74c3c;
        font-size: 14px;
        margin-top: 4px;
        margin-bottom: 8px;
        font-weight: 500;
    }
    
    .input-error {
        border-color: #e74c3c !important;
        box-shadow: 0 0 0 2px rgba(231, 76, 60, 0.1);
    }
    
    .meeting-link-help {
        font-size: 13px;
        color: #666;
        margin-top: 4px;
        margin-bottom: 8px;
        line-height: 1.4;
    }
    
    .meeting-link-help a {
        color: #14695B;
        text-decoration: none;
        font-weight: 500;
        transition: color 0.2s ease;
    }
    
    .meeting-link-help a:hover {
        color: #0f5248;
        text-decoration: underline;
    }

    .qr-code-card {
        width: 100%;
        box-sizing: border-box;
        margin-block: 20px;
        padding: 16px;
        border: 1px solid #d7ddd9;
        border-radius: 8px;
        background: #f8f9fa;
    }

    .qr-code-card h3 {
        margin: 0 0 8px;
        color: #14695B;
        font-size: 17px;
    }

    .qr-code-help, .qr-code-status {
        margin: 0 0 12px;
        color: #666;
        font-size: 14px;
        line-height: 1.4;
    }

    .qr-code-card input[type="file"] {
        width: 100%;
    }

    .qr-code-actions {
        display: flex;
        gap: 10px;
        flex-wrap: wrap;
    }

    .qr-code-actions button {
        width: auto;
        min-width: 150px;
        margin-top: 12px;
    }

    .qr-code-actions .secondary-button {
        background: white;
        color: #14695B;
        border-color: #14695B;
    }

    .qr-code-success {
        color: #14695B;
        margin-top: 10px;
        font-size: 14px;
        font-weight: 600;
    }

    .qr-code-preview {
        margin-top: 14px;
    }

    .qr-code-preview img {
        display: block;
        max-width: 100%;
        max-height: 500px;
        object-fit: contain;
        border: 1px solid #ddd;
        border-radius: 8px;
        background: white;
    }
    
    /* Mobile responsiveness */
    @media (max-width: 768px) {
        .qr-code-card {
            margin-block: 16px;
        }

        .container {
            padding: 0 12px;
            gap: 16px;
        }
        
        .teacher-section {
            padding-top: 20px;
            margin-top: 20px;
        }
        
        .subjects-container {
            max-height: 150px;
        }
        
        .subject-label {
            padding: 12px;
        }
        
        .checkmark {
            height: 28px;
            width: 28px;
            margin-right: 15px;
        }
        
        .subject-label input:checked ~ .checkmark:after {
            left: 8px;
            top: 3px;
            width: 7px;
            height: 14px;
        }
        
        .subject-text {
            font-size: 16px;
        }
        
        button {
            height: 48px;
            font-size: 15px;
        }
    }
</style>

<svelte:window on:beforeunload={beforeLeaving} />

<NavbarReusable label="Mano Profilis" />
<FilterButtonsForSettingsPage currentFilter='Paskyra'></FilterButtonsForSettingsPage>
<div class="container">
    <div class="input-group">
        <label for="email">Keisti el.pašto adresą</label>
        <input id="email" type="email" bind:value={email} disabled={!accountLoaded || accountNeedsLogin} placeholder="john@email.com">
    </div>

    <div class="input-group">
        <label for="phone">Telefono numeris</label>
        <input id="phone" type="text" bind:value={phoneNumber} disabled={!accountLoaded || accountNeedsLogin} placeholder="+370 600 12345">
    </div>

    <div class="checkbox-group">
        <label for="sms-notification">Priminimai apie rezervuotą pamoką</label>
        <div class="smaller-part">
            <input id="sms-notification" type="checkbox" bind:checked={receiveMessages} disabled={!accountLoaded || accountNeedsLogin}>
            <span for="sms-notification">Gauti SMS žinutes</span>
        </div>
    </div>

    <section class="daily-reminders" aria-labelledby="daily-reminders-heading">
        <h2 id="daily-reminders-heading">Kasdieniai priminimai el. paštu</h2>
        {#if accountLoadError}
            <p role="alert">{accountLoadError}</p>
            {#if accountNeedsLogin}
                <a href="#/about">Prisijungti</a>
            {:else}
                <button type="button" on:click={fetchInitialData}>Bandyti dar kartą</button>
            {/if}
        {:else if !accountLoaded}
            <p role="status">Įkeliami paskyros nustatymai…</p>
        {/if}
        <label for="daily-reminders-enabled">
            <input id="daily-reminders-enabled" type="checkbox" bind:checked={receiveDailyReminders}
                disabled={!accountLoaded || accountNeedsLogin} aria-describedby="daily-reminders-help">
            <span>Gauti kasdienius pamokų priminimus el. paštu</span>
        </label>
        <p id="daily-reminders-help">Kasdien 20:00 Lietuvos laiku siunčiamas laiškas su rytojaus pamokų priminimu.</p>
    </section>

    <!-- Student grade section for non-teacher users -->
    {#if !isTeacher}
        <div class="input-group">
            <label for="student-grade">Klasė</label>
            <input 
                id="student-grade" 
                type="text" 
                bind:value={studentGrade} disabled={!accountLoaded || accountNeedsLogin}
                placeholder="Įveskite klasę (pvz.:, 9, 10, 11, 12, university)"
            >
        </div>
    {/if}

    {#if accountLoaded}
        <AgreementSection />
    {/if}

    <!-- Teacher-specific section -->
    {#if isTeacher}
        <div class="teacher-section">
            <div class="section-title">Mokytojo informacija</div>
            
            <div class="input-group">
                <label for="indiv-lic">Individualios veiklos pažyma</label>
                <input id="indiv-lic" name="individual-activity-certificate" type="text" inputmode="numeric" autocomplete="off" autocapitalize="none" autocorrect="off" spellcheck="false" data-lpignore="true" data-1p-ignore="true" bind:value={indivLic} disabled={!accountLoaded || accountNeedsLogin} placeholder={indivLic || 'Įveskite licencijos numerį'}>
            </div>

            <div class="input-group">
                <label for="bank-nr">Banko sąskaitos numeris</label>
                <input id="bank-nr" type="text" bind:value={bankNr} disabled={!accountLoaded || accountNeedsLogin} placeholder={bankNr || 'Įveskite banko sąskaitos numerį'}>
            </div>

            <div class="input-group">
                <label for="meeting-link">Susitikimo kambarys (nuoroda)</label>
                <div class="meeting-link-help">
                    Kaip rasti nuorodą: <a href="https://meet.google.com/landing" target="_blank" rel="noopener noreferrer">Google Meet</a> → New Meeting → Meeting for later → Invite link
                </div>
                <input id="meeting-link" type="url" bind:value={meetingLink} disabled={!accountLoaded || accountNeedsLogin} placeholder={meetingLink || 'https://meet.google.com/pwk-nxkw-rjo'}>
            </div>

            <div class="input-group">
                <label for="desired-student-count">Kiek norite pamokų per <strong>savaitę</strong>?</label>
                <input
                    id="desired-student-count"
                    type="number"
                    min="0"
                    step="1"
                    bind:value={desiredStudentCount} disabled={!accountLoaded || accountNeedsLogin}
                    placeholder="Įveskite norimą mokinių skaičių"
                >
            </div>

            <div class="qr-code-card">
                <h3>Teisėto darbo su vaikais QR kodas</h3>
                <p class="qr-code-help">Įkelkite JPEG, PNG, WebP arba PDF formato QR kodo failą (iki 10 MiB).</p>
                {#if workWithChildrenQrCode.exists}
                    <p class="qr-code-status">QR kodas įkeltas {formatQrUploadDate(workWithChildrenQrCode.uploadedAt)}. Naujas failas pakeis esamą.</p>
                {:else}
                    <p class="qr-code-status">QR kodas dar neįkeltas.</p>
                {/if}
                <input bind:this={qrCodeInput} type="file" accept={TEACHER_WORK_QR_ACCEPT} on:change={selectQrCode} disabled={qrCodeUploading}>
                <div class="qr-code-actions">
                    <button type="button" on:click={uploadQrCode} disabled={!qrCodeFile || qrCodeUploading}>
                        {qrCodeUploading ? 'Įkeliama...' : (workWithChildrenQrCode.exists ? 'Pakeisti QR kodą' : 'Įkelti QR kodą')}
                    </button>
                    {#if workWithChildrenQrCode.exists}
                        <button type="button" class="secondary-button" on:click={viewQrCode} disabled={qrCodeViewing}>
                            {qrCodeViewing ? 'Kraunama...' : 'Peržiūrėti QR kodą'}
                        </button>
                    {/if}
                </div>
                {#if qrCodeMessage}<div class="qr-code-success" role="status">{qrCodeMessage}</div>{/if}
                {#if qrCodeError}<div class="error-message" role="alert">{qrCodeError}</div>{/if}
                {#if qrCodePreviewUrl}
                    <div class="qr-code-preview">
                        <img src={qrCodePreviewUrl} alt="Teisėto darbo su vaikais QR kodas">
                        <div class="qr-code-actions">
                            <button type="button" class="secondary-button" on:click={closeQrCodePreview}>Uždaryti</button>
                        </div>
                    </div>
                {/if}
            </div>

            <div class="input-group">
                <label for="subject-search">Dėstomi dalykai</label>

                <input
                    id="subject-search"
                    class="subject-search"
                    type="search"
                    bind:value={subjectSearch}
                    placeholder="Ieškoti dalyko..."
                    aria-controls="subjects-section"
                    autocomplete="off"
                >

                <div class="subjects-container" id="subjects-section">
                    <div style="margin-bottom: 8px; font-size: 13px; color: #888; font-style: italic;">Formatas: pamoka + kalba (pvz.: "Lietuvių k. Angl." = lietuvių kalba, dėstoma angliškai)</div>
                    <div style="margin-bottom: 12px; font-size: 14px; color: #666;">Pasirinkite dalykus:</div>
                    {#if filteredSubjects.length > 0}
                        {#each filteredSubjects as subject}
                            <div class="subject-item">
                                <label class="subject-label">
                                    <input
                                        type="checkbox"
                                        checked={selectedSubjects.includes(subject)}
                                        on:change={() => toggleSubject(subject)}
                                    >
                                    <span class="checkmark"></span>
                                    <span class="subject-text">{subject}</span>
                                </label>
                            </div>
                        {/each}
                    {:else}
                        <div class="no-subject-results" role="status">Dalykų nerasta.</div>
                    {/if}
                </div>
            </div>

            <div class="input-group">
                <label for="subject-grades-section">Klasių intervalai pagal dalyką (pvz.: "6-9" arba "10-12")</label>
                {#if selectedSubjects.length > 0}
                    <div class="subject-grades" id="subject-grades-section">
                        {#each selectedSubjects as subject}
                            <div class="subject-grade-row">
                                <div class="subject-grade-header">
                                    <span class="subject-grade-name">{subject}</span>
                                    <span
                                        class="remove-subject-grade"
                                        on:click={() => removeSubject(subject)}
                                        on:keydown={(e) => e.key === 'Enter' && removeSubject(subject)}
                                        tabindex="0"
                                        role="button"
                                        aria-label="Remove {subject}"
                                    >×</span>
                                </div>
                                <input
                                    type="text"
                                    bind:value={subjectGrades[subject]} disabled={!accountLoaded || accountNeedsLogin}
                                    placeholder="Įveskite klasių intervalą (pvz.: 6-9)"
                                    class:input-error={gradeErrors[subject]}
                                    on:input={() => clearGradeError(subject)}
                                >
                                {#if gradeErrors[subject]}
                                    <div class="error-message">{gradeErrors[subject]}</div>
                                {/if}
                            </div>
                        {/each}
                    </div>
                {:else}
                    <div class="no-subjects-hint">Pasirinkite dalykus, kad galėtumėte įvesti klasių intervalus.</div>
                {/if}
            </div>
        </div>
    {/if}

    <div class="autosave-status" role="status" aria-live="polite">
        {#if saveStatus.saving}Išsaugoma…
        {:else if saveStatus.dirty}Yra neišsaugotų pakeitimų.
        {:else if saveStatus.edited}Pakeitimai išsaugoti.
        {:else if accountLoaded}Pakeitimai išsaugomi automatiškai.{/if}
    </div>
    {#if saveStatus.error && !accountNeedsLogin}
        <div class="error-message" role="alert">{saveStatus.error}</div>
        <button type="button" on:click={() => autosave.flush()}>Bandyti išsaugoti dar kartą</button>
    {/if}

    
</div>
